import type { actions, definitions } from '@luxuryescapes/contract-flights-charter';

import type {
  CharterInventoryFilters,
  CharterInventoryFlight,
  CharterLoyaltyPointMultiplierFilters,
  CharterLoyaltyPointMultiplierResult,
  CharterLoyaltyPointMultiplierUpdateRequestBody,
} from '~/components/Flights/Charter/charter.types';

import { json_headers, request } from './common';

const BASE_PATH = `${window.configs.API_HOST}/api/flights-charter/v1`;

export type CharterDetailsGetRes =
  actions['reservationGetDetailsByPnr']['responses']['200']['content']['application/json'];

export const fetchCharterDetails = async (pnr: string): Promise<CharterDetailsGetRes['result']> => {
  const uri = `${BASE_PATH}/reservations/${pnr.trim().toUpperCase()}/details`;
  const response: CharterDetailsGetRes = await request(uri, { method: 'GET' });
  return response.result;
};

export type CharterReservationUpdateRes =
  actions['reservationUpdate']['responses']['200']['content']['application/json'];
export type CharterReservationReqBody = actions['reservationUpdate']['requestBody']['content']['application/json'];

export const charterReservationUpdate = async (
  payload: CharterReservationReqBody & { pnr: string },
): Promise<CharterReservationUpdateRes> => {
  const uri = `${BASE_PATH}/reservations/${payload.pnr.trim().toUpperCase()}`;
  return request(uri, {
    method: 'PATCH',
    headers: json_headers,
    body: JSON.stringify(payload),
  });
};

export type CharterInventoryGetRes =
  actions['departuresGetInventory']['responses']['200']['content']['application/json'];

export const fetchCharterInventory = async (
  filters: CharterInventoryFilters,
): Promise<Array<CharterInventoryFlight>> => {
  const uri = `${BASE_PATH}/departures/inventory`;
  const response: CharterInventoryGetRes = await request(uri, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify(filters),
  });

  return response.result;
};

export const fetchCharterLoyaltyPointMultipliers = async (
  params: CharterLoyaltyPointMultiplierFilters,
): Promise<Array<CharterLoyaltyPointMultiplierResult>> => {
  const uri = `${BASE_PATH}/departures/loyalty-point-multipliers/query`;
  const response: { result: Array<CharterLoyaltyPointMultiplierResult> } = await request(uri, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify(params),
  });
  return response.result;
};

export const updateCharterLoyaltyPointMultipliers = async (
  payload: CharterLoyaltyPointMultiplierUpdateRequestBody,
): Promise<CharterLoyaltyPointMultiplierUpdateRequestBody['items']> => {
  const uri = `${BASE_PATH}/departures/loyalty-point-multipliers`;
  const response: { result: CharterLoyaltyPointMultiplierUpdateRequestBody['items'] } = await request(uri, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify(payload),
  });

  return response.result;
};

export type CharterInventoryManagementParams = actions['departuresInventoryManagementGet']['parameters']['query'];
export type CharterInventoryManagementRow = definitions['CharterInventoryManagementRow'];
export type CharterInventoryManagementGetRes =
  actions['departuresInventoryManagementGet']['responses']['200']['content']['application/json'];
export type CharterInventoryManagementUpdatePayload =
  actions['departuresInventoryManagementUpdate']['requestBody']['content']['application/json'];
export type CharterInventoryManagementUpdateRes =
  actions['departuresInventoryManagementUpdate']['responses']['200']['content']['application/json'];

export const fetchCharterInventoryManagement = async (
  params: CharterInventoryManagementParams,
  options?: { signal?: AbortSignal },
): Promise<CharterInventoryManagementGetRes> => {
  const queryParams = new URLSearchParams({
    departingDepAirport: params.departingDepAirport,
    departingArrAirport: params.departingArrAirport,
    returningDepAirport: params.returningDepAirport,
    returningArrAirport: params.returningArrAirport,
  });

  if (params.offset !== undefined) {
    queryParams.set('offset', String(params.offset));
  }
  if (params.limit !== undefined) {
    queryParams.set('limit', String(params.limit));
  }

  const uri = `${BASE_PATH}/departures/inventory-management?${queryParams.toString()}`;
  return request(uri, {
    method: 'GET',
    signal: options?.signal,
  });
};

export const updateCharterInventoryManagement = async (
  payload: CharterInventoryManagementUpdatePayload,
): Promise<CharterInventoryManagementUpdateRes> => {
  const uri = `${BASE_PATH}/departures/inventory-management`;
  return request(uri, {
    method: 'PUT',
    headers: json_headers,
    body: JSON.stringify(payload),
  });
};

/** Response type for fare family pricing GET */
export type FareFamilyPricingGetRes =
  actions['getFareFamilyPricing']['responses']['200']['content']['application/json'];

/** Query parameters for fare family pricing GET */
export type FareFamilyPricingParams = actions['getFareFamilyPricing']['parameters']['query'];

/**
 * Fetches fare family pricing for charter flights by route.
 * Returns pricing data for both departing and returning flights.
 *
 * Accepts an optional `AbortSignal` so callers (e.g. TanStack Query) can
 * cancel in-flight requests on rapid re-fetches.
 */
export const fetchCharterFareFamilyPricing = async (
  params: FareFamilyPricingParams,
  options?: { signal?: AbortSignal },
): Promise<FareFamilyPricingGetRes> => {
  const queryParams = new URLSearchParams({
    departingDepAirport: params.departingDepAirport,
    departingArrAirport: params.departingArrAirport,
    returningDepAirport: params.returningDepAirport,
    returningArrAirport: params.returningArrAirport,
  });

  if (params.offset !== undefined) {
    queryParams.set('offset', String(params.offset));
  }
  if (params.limit !== undefined) {
    queryParams.set('limit', String(params.limit));
  }

  const uri = `${BASE_PATH}/fare-families/pricing?${queryParams.toString()}`;
  const response: FareFamilyPricingGetRes = await request(uri, {
    method: 'GET',
    signal: options?.signal,
  });
  return response;
};

/** Response type for fare family pricing PUT */
export type FareFamilyPricingUpdateRes =
  actions['updateFareFamilyPricing']['responses']['200']['content']['application/json'];

/** Payload type for fare family pricing PUT (parallel arrays) */
export type FareFamilyPricingUpdatePayload =
  actions['updateFareFamilyPricing']['requestBody']['content']['application/json'];

/**
 * Bulk updates fare family pricing for charter flights.
 * Accepts parallel arrays of pricing data keyed by flightInstanceIds.
 */
export const updateCharterFareFamilyPricing = async (
  payload: FareFamilyPricingUpdatePayload,
): Promise<FareFamilyPricingUpdateRes> => {
  const uri = `${BASE_PATH}/fare-families/pricing`;
  const response: FareFamilyPricingUpdateRes = await request(uri, {
    method: 'PUT',
    headers: json_headers,
    body: JSON.stringify(payload),
  });
  return response;
};

/** Response type for fare family pricing preview POST */
export type FareFamilyPricingPreviewRes =
  actions['previewFareFamilyPricing']['responses']['200']['content']['application/json'];

/** Payload type for fare family pricing preview POST */
export type FareFamilyPricingPreviewPayload =
  actions['previewFareFamilyPricing']['requestBody']['content']['application/json'];

/**
 * Previews fare family pricing calculations without persisting to database.
 */
export const previewCharterFareFamilyPricing = async (
  payload: FareFamilyPricingPreviewPayload,
): Promise<FareFamilyPricingPreviewRes> => {
  const uri = `${BASE_PATH}/fare-families/pricing/preview`;
  const response: FareFamilyPricingPreviewRes = await request(uri, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify(payload),
  });
  return response;
};

// Cabin Upgrade

export type CabinClass = definitions['CabinUpgradeCombinedRow']['fromCabinClass'];
export type CombinedUpgradeApiRow = definitions['CabinUpgradeCombinedRow'];
export type CabinUpgradePricingGetRes =
  actions['getCabinUpgradePricing']['responses']['200']['content']['application/json'];
export type CabinUpgradePricingGetParams = actions['getCabinUpgradePricing']['parameters']['query'];
export type CabinUpgradeUpsertPayload = actions['upsertCabinUpgrades']['requestBody']['content']['application/json'];
export type CabinUpgradeEligibilityUpsertItem = CabinUpgradeUpsertPayload['eligibility'][number];
export type CabinUpgradePricingUpsertItem = CabinUpgradeUpsertPayload['pricing'][number];
export type CabinUpgradeUpsertRes = actions['upsertCabinUpgrades']['responses']['200']['content']['application/json'];

export const fetchCabinUpgradePricing = async (
  params: CabinUpgradePricingGetParams,
): Promise<CabinUpgradePricingGetRes> => {
  const queryParams = new URLSearchParams();
  if (params.depAirport) queryParams.set('depAirport', params.depAirport);
  if (params.arrAirport) queryParams.set('arrAirport', params.arrAirport);
  if (params.departureDateFrom) queryParams.set('departureDateFrom', params.departureDateFrom);
  if (params.departureDateTo) queryParams.set('departureDateTo', params.departureDateTo);
  if (params.offset !== undefined) queryParams.set('offset', String(params.offset));
  if (params.limit !== undefined) queryParams.set('limit', String(params.limit));

  const uri = `${BASE_PATH}/cabin-upgrades/pricing?${queryParams.toString()}`;
  const response: CabinUpgradePricingGetRes = await request(uri, { method: 'GET' });
  return response;
};

export const upsertCabinUpgrades = async (payload: CabinUpgradeUpsertPayload): Promise<CabinUpgradeUpsertRes> => {
  const uri = `${BASE_PATH}/cabin-upgrades`;
  return request(uri, {
    method: 'PUT',
    headers: json_headers,
    body: JSON.stringify(payload),
  });
};
